import CloseButton from '@CS/assets/close.svg?react';
import { DELTA_UPDATE } from '@CS/utils/Number';
import { type SoundName, play } from 'cuelume';
import { useClassNames } from '@CS/hooks';
type ClickEvent = MouseEvent<HTMLElement>;
type Children = ReactNode | ReactNode[];
import CSS from './Popup.module.css';

import {
  useRef,
  useEffect,
  forwardRef,
  useCallback,
  type ReactNode,
  type MouseEvent,
  type ForwardedRef,
  type CSSProperties,
  useImperativeHandle
} from 'react';

export type PopupProps = {
  customStyle?: CSSProperties & {
    [key: `--${string}`]: string | number;
  };

  allowClickOutside?: boolean;
  onBeforeClose?: () => void;
  opacity?: number | string;

  onClose?: () => void;
  fullscreen?: boolean;
  hideClose?: boolean;
  children?: Children;

  className?: string;
  sound?: SoundName;
  close?: boolean;
  width?: string;
};

export const Popup = forwardRef(
  (
    {
      allowClickOutside = false,
      onClose = () => void 0,
      fullscreen = false,
      hideClose = false,

      sound = 'bloom',
      opacity = 0.25,
      close = false,
      width = '85%',

      onBeforeClose,
      customStyle,
      className,
      children
    }: PopupProps,
    ref: ForwardedRef<HTMLDialogElement>
  ) => {
    const popupClassNames = useClassNames([
      fullscreen && CSS.fullscreen,
      CSS.popupContainer,
      className
    ]);

    const dialog = useRef<HTMLDialogElement>(null);
    useImperativeHandle(ref, () => dialog.current!);

    const onClickOutside = (event: ClickEvent) =>
      event.target === event.currentTarget &&
      !allowClickOutside &&
      !hideClose &&
      onPopupClose();

    const onPopupClose = useCallback(
      (sound?: boolean) => {
        const delay =
          +getComputedStyle(dialog.current!)
            .getPropertyValue('--transition-duration')
            .slice(0.0, -1.0) * 1e3;

        setTimeout(onClose, delay);
        dialog.current?.close();
        sound && play('press');
        onBeforeClose?.();
        play('whisper');
      },
      [onClose, onBeforeClose]
    );

    useEffect(() => {
      close && onPopupClose();
    }, [close, onPopupClose]);

    useEffect(() => {
      setTimeout(() => dialog.current?.showModal(), DELTA_UPDATE);
      sound && play(sound);
    }, [sound]);

    return (
      <dialog
        ref={dialog}
        onClick={onClickOutside}
        className={popupClassNames}
        style={{ '--opacity': opacity, width } as CSSProperties}
      >
        {!hideClose && <CloseButton onClick={onPopupClose.bind(null, true)} />}

        <div className={CSS.popupContent} style={customStyle}>
          {children}
        </div>
      </dialog>
    );
  }
);
